import React, {memo} from "react";
import DropdownInput from "./DropdownInput";
import {InputType} from "../types/InputType";
import OutlineInput from "./OutlineInput";
import {Button} from "@material-tailwind/react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {Input as InputForm, Input} from "../types/Input";
import {BuilderInputType} from "../types/BuilderInputType";
import {useTranslation} from "react-i18next";

interface InputBuilderProps {
    inputs: Input[] | undefined,
    type: BuilderInputType,
    validated: boolean,
    dependsLabels: (index: number, key: string, type: BuilderInputType) => { code: string, name: string }[]
    removeField: (index: number, type: BuilderInputType) => void,
    onDependsChanged: (s: string, input: InputForm, type: BuilderInputType) => void,
    onInputTypeChanged: (s: InputType, input: InputForm, type: BuilderInputType) => void,
    onInputKeyChanged: (e: React.ChangeEvent<HTMLInputElement> | undefined, index: number, field: string, type: BuilderInputType) => void,
    addOption: (index: number, type: BuilderInputType) => void,
    removeOption: (index: number, optionID: number, type: BuilderInputType) => void,
    onOptionChanged: (e: React.ChangeEvent<HTMLInputElement> | undefined, index: number, optionID: number, optionKey: string, type: BuilderInputType) => void
}

const InputBuilder = ({
                          inputs,
                          type,
                          validated,
                          dependsLabels,
                          addOption,
                          onOptionChanged,
                          onDependsChanged,
                          removeField,
                          removeOption,
                          onInputKeyChanged,
                          onInputTypeChanged
                      }: InputBuilderProps) => {
    const {t} = useTranslation();
    const inputTypes = [
        {name: InputType.input.substring(0, 1).toUpperCase() + InputType.input.substring(1), code: InputType.input},
        {name: InputType.email.substring(0, 1).toUpperCase() + InputType.email.substring(1), code: InputType.email},
        {name: InputType.phone.substring(0, 1).toUpperCase() + InputType.phone.substring(1), code: InputType.phone},
        {name: InputType.number.substring(0, 1).toUpperCase() + InputType.number.substring(1), code: InputType.number},
        {
            name: InputType.password.substring(0, 1).toUpperCase() + InputType.password.substring(1),
            code: InputType.password
        },
        {name: InputType.select.substring(0, 1).toUpperCase() + InputType.select.substring(1), code: InputType.select},
        {
            name: InputType.textarea.substring(0, 1).toUpperCase() + InputType.textarea.substring(1),
            code: InputType.textarea
        },
    ]

    if (!inputs)
        return <></>

    return <>{inputs.map((input, index) => {
        return <div key={`input-${type}-${index}`}
                    className="rounded-lg border border-blue-gray-500 p-4 mb-4">
            {type === BuilderInputType.currency && <h4 className="mb-4 text-sm bg-info text-white p-4 rounded-md">{t('input_builder.repeat.message')}</h4>}
            <DropdownInput name={t('input_builder.type')}
                           onChange={(s) => onInputTypeChanged(s as InputType, input, type)}
                           selected={input.type} list={inputTypes}/>
            <OutlineInput className="mt-4" onChange={(e) => onInputKeyChanged(e, index, 'key', type)} name={t('input_builder.key')}
                          value={input.key} validated={validated} fieldName={'key'}/>
            <OutlineInput className="mt-4" onChange={(e) => onInputKeyChanged(e, index, 'label', type)}
                          name={t('input_builder.label')} value={input.label} validated={validated} fieldName={'label'}/>
            {input.type !== InputType.select ?
                <OutlineInput className="mt-4" onChange={(e) => onInputKeyChanged(e, index, 'regex', type)}
                              name={t('input_builder.regex')} value={input.regex ?? ''} validated={validated}
                              fieldName={'regex'}/> : ''}
            <div className="grid grid-cols-2 gap-2 mt-4">
                <div>
                    <DropdownInput name={t('input_builder.depends')} selected={input.depends}
                                   onChange={(s) => onDependsChanged(s.toString(), input, type)}
                                   list={dependsLabels(index, input.key, type)}
                    />
                </div>
                <OutlineInput onChange={(e) => onInputKeyChanged(e, index, 'show_when', type)} name={t('input_builder.value')}
                              value={input.show_when} validated={validated} fieldName={'show_when'}/>
            </div>
            {input.type === InputType.select && (input.options || (!input.options && !input.options_method)) ?
                <div className="mt-8 p-4 border border-gray-700 rounded-lg">
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <h4 className="text-sm font-bold">{t('input_builder.select_options')}</h4>
                        </div>
                        <div className="text-right">
                            <Button size="sm" className="shadow-none hover:shadow-none hover:bg-black"
                                    onClick={() => addOption(index, type)}><FontAwesomeIcon icon={solid("plus")}
                                                                                            className="w-5"/></Button>
                        </div>
                    </div>
                    {input.options && input.options.map((option, optionIndex) => {
                        return <div key={`option-${type}-${input.key}-${optionIndex}`}
                                    className="grid grid-cols-5 gap-2 mt-4">
                            <OutlineInput className="mr-0 col-span-2 builder-option-input"
                                          onChange={(e) => onOptionChanged(e, index, optionIndex, 'id', type)}
                                          name={t('input_builder.key')}
                                          value={option.id} validated={validated}
                                          fieldName={`option_${type}_` + index + '_' + optionIndex + '_key'}/>
                            <OutlineInput className="mr-0 col-span-2 builder-option-input"
                                          onChange={(e) => onOptionChanged(e, index, optionIndex, 'name', type)}
                                          name={t('input_builder.value')}
                                          value={option.name} validated={validated}
                                          fieldName={`option_${type}_ ${index}_${optionIndex}_value`}/>
                            <div className="builder-option-input text-right">
                                <Button size="sm"
                                        onClick={() => removeOption(index, optionIndex, type)}><FontAwesomeIcon
                                    icon={solid("trash")}
                                    className="w-5"/></Button>
                            </div>
                        </div>
                    })}
                </div> : ''}
            {input.type === InputType.select && (input.options_method || (!input.options && !input.options_method)) ?
                <OutlineInput className="mt-4"
                              onChange={(e) => onInputKeyChanged(e, index, 'options_method', type)}
                              name={t('input_builder.options_method')} value={input.options_method} validated={validated}
                              fieldName={'options_method'}/> : ''}
            <div className="text-right mt-4">
                <Button size="sm" className="bg-black"
                        onClick={() => removeField(index, type)}><FontAwesomeIcon icon={solid("trash")}
                                                                                  className="w-5 text-red-300"/></Button>
            </div>
        </div>
    })}</>
};
InputBuilder.displayName = 'InputBuilder';
export default memo(InputBuilder);
